import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from '../routing';
import api from '../utils/api';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/AuthShell';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

// Landing page for both email links: proving a NEW signup address ("verify")
// and completing an address CHANGE ("change"). One component, because the two
// screens differ only in which endpoint they call and what the success line
// says — and a second near-identical copy is how the two drift apart.
//
// The confirmation is a POST issued by this page, not a GET the link itself
// performs. Corporate mail scanners and link previewers fetch every URL in a
// message before a human ever sees it, so a GET that applied the change would be
// applied by a robot — the same reason the newsletter confirmation was rewritten
// to never mutate consent on a GET.
export default function ConfirmEmail({ mode = 'change' }) {
  const ENDPOINT = mode === 'verify'
    ? '/auth/verify-email/confirm'
    : '/auth/email-change/confirm';
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const token = params.get('token');

  const [state, setState] = useState(token ? 'working' : 'invalid');
  const [email, setEmail] = useState(null);
  const [message, setMessage] = useState(null);
  // React 18 StrictMode mounts effects twice in development; without this the
  // second run would consume the (single-use) token and report failure.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    api.post(ENDPOINT, { token })
      .then((res) => { setEmail(res.email); setState('done'); })
      .catch((err) => {
        setMessage(err?.message || err?.error || null);
        setState('failed');
      });
  }, [token, ENDPOINT]);

  const icon = {
    working: <Loader2 size={40} color={theme.gold} className="gg-spin" />,
    done: <CheckCircle2 size={40} color={theme.greenSoft} />,
    failed: <XCircle size={40} color={theme.redSoft} />,
    invalid: <XCircle size={40} color={theme.redSoft} />,
  }[state];

  const title = {
    working: t('confirmEmail.working'),
    done: t(mode === 'verify' ? 'confirmEmail.verifiedTitle' : 'confirmEmail.doneTitle'),
    failed: t('confirmEmail.failedTitle'),
    invalid: t('confirmEmail.failedTitle'),
  }[state];

  return (
    <AuthShell maxWidth={460}>
      <style>{`
        @import url('${FONTS}');
        @keyframes gg-spin { to { transform: rotate(360deg); } }
        .gg-spin { animation: gg-spin 0.8s linear infinite; }
      `}</style>

      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>{icon}</div>

        <h1 style={{ fontFamily: theme.font.display, fontSize: 28, fontWeight: 400, color: theme.ink, margin: '0 0 12px' }}>
          {title}
        </h1>

        {state === 'done' && (
          <>
            <p style={{ fontSize: 14, color: theme.grey550, lineHeight: 1.7, margin: '0 0 8px' }}>
              {t(mode === 'verify' ? 'confirmEmail.verifiedBody' : 'confirmEmail.doneBody')}
            </p>
            <p style={{ fontSize: 15, color: theme.ink, margin: '0 0 28px' }}><strong>{email}</strong></p>
            <Link to={mode === 'verify' ? '/browse' : '/profile'} style={{ display: 'inline-block', background: theme.ink, color: theme.white, padding: '14px 32px', textDecoration: 'none', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {t(mode === 'verify' ? 'confirmEmail.toBrowse' : 'confirmEmail.toProfile')}
            </Link>
          </>
        )}

        {(state === 'failed' || state === 'invalid') && (
          <>
            <p style={{ fontSize: 14, color: theme.grey550, lineHeight: 1.7, margin: '0 0 28px' }}>
              {message || t('confirmEmail.failedBody')}
            </p>
            <Link to="/profile" style={{ display: 'inline-block', border: `1px solid ${theme.sand}`, color: theme.ink, padding: '14px 32px', textDecoration: 'none', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {t('confirmEmail.tryAgain')}
            </Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}
